/* -------------------------- Design imports start -------------------------- */
import { AddRounded, DeleteRounded } from '@mui/icons-material'
import {
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import GetConstant from './utils/constants'
import Button from '../../components/Button'
import SearchSelectInput from '../../components/inputs/SearchSelectInput'
import MultilevelDrawer from '../../components/layout/MultilevelDrawer'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */

import LogTool from '../../logger/logTools'
import { getAddressRepresentation, missingRequiredFormFields } from '../../utils/functions'
import { type Address, type Contact, type SelectOption } from '../../utils/types'
import { Form, type FormFieldType } from '../Form'
import { type AddressListFieldObject } from './utils/types'
import { useAllAddresses } from '../Address/hooks/useAddressQueries'
import { useUserContext } from '../../utils/context'
import { useCreateContact, useUpdateContact } from '../Contact/hooks/useContactMutations'
/* ------------------------- Functional imports end ------------------------- */

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: Partial<Contact> | Contact | undefined
    setState: React.Dispatch<React.SetStateAction<Partial<Contact> | Contact | undefined>>
  }
  onClose?: () => void
  onConfirm?: (input: Partial<Contact>) => Promise<'SUCCESS' | 'ERROR'>
  enableEdit?: boolean
  onEditConfirm?: (input: Partial<Contact>) => Promise<'SUCCESS' | 'ERROR'>
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function ContactDrawer(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'ContactDrawer', enable: true, logLevel: 'warn' })
  const {
    open,
    formState,
    setOpen,
    onClose = () => null,
    onConfirm,
    enableEdit = false,
    onEditConfirm,
  } = props

  const { t } = useTranslation()
  const { user } = useUserContext()
  const isGuestUser = user?.isGuestUser ?? false
  const createContactMutation = useCreateContact()
  const updateContactMutation = useUpdateContact()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [dataLoaded, setDataLoaded] = useState(false)
  const [inEditMode, setInEditMode] = useState(false)
  const [, setOpenAddressDrawer] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const uniqueKeyStore = useRef(0)
  const addressStore = useRef<Address[]>([])
  const [addressOptions, setAddressOptions] = useState<SelectOption[]>([])
  const [addressListForm, setAddressListForm] = useState<AddressListFieldObject[]>([
    generateAddressFieldObject('addAddress'),
  ])
  // key of the AddressListFieldObject through that the addAddressOption callback was fired
  const [, setNewAddressReceiver] = useState<string | undefined>()
  const isSaving = createContactMutation.isPending || updateContactMutation.isPending
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  // Disable addresses query for guest users - they don't have access to these resources
  const {
    data: addressesData,
    isError: addressesError,
    error: addressesErrorObj,
  } = useAllAddresses({}, !isGuestUser)

  useEffect(() => {
    if (addressesError && addressesErrorObj) {
      log.error('Error fetching addresses', addressesErrorObj)
    }
  }, [addressesError, addressesErrorObj, log])

  useEffect(() => {
    if (!addressesData) return
    addressStore.current = addressesData
    setAddressOptions(
      addressesData.map(address => ({
        value: address.self,
        label: getAddressRepresentation(address, true, false),
      }))
    )
  }, [addressesData])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Generates a unique element key (ascending number).
   * @returns Unique Key
   */
  function getUniqueKey(): string {
    const key = uniqueKeyStore.current
    uniqueKeyStore.current += 1
    return String(key)
  }
  /**
   * Creates a new, unique AddressListFieldObject that can safely be inserted into the addressListForm.
   */
  function generateAddressFieldObject(
    type: 'addAddress' | 'selectAddress' | 'savedAddress' = 'selectAddress',
    value?: SelectOption
  ): AddressListFieldObject {
    return {
      key: `address${getUniqueKey()}`,
      type: type,
      value: value,
    }
  }

  /**
   * Determines if there are any empty address select input fields.
   * @returns
   */
  function missingAddressSelectInput(): boolean {
    const selectFields = addressListForm.filter(fieldObj => fieldObj.type === 'selectAddress')
    if (selectFields.length === 0) {
      return false
    }
    return selectFields.some(fieldObj => !fieldObj.value)
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (preventOnClose = false): void => {
    // reset modal to initial state
    formState.setState(undefined)
    uniqueKeyStore.current = 0
    setAddressListForm([generateAddressFieldObject('addAddress')])
    // Do not reset prevOpen to false here, as this might prevent re-initialization when opening again with same props
    // Instead, we rely on the open prop changing to trigger the useEffect/logic
    // setPrevOpen(false)
    setInEditMode(false)
    setOpen(false)

    !preventOnClose && onClose()
  }

  const handleConfirm = async (): Promise<void> => {
    const contactState = formState.state ?? {}
    const preparedAddresses = addressListForm
      .filter(
        (fieldObj: AddressListFieldObject) =>
          (fieldObj.type === 'selectAddress' || fieldObj.type === 'savedAddress') &&
          typeof fieldObj.value !== 'undefined'
      )
      .map((fieldObj: AddressListFieldObject) =>
        fieldObj.value ? fieldObj.value.value : undefined
      )
      .filter((value): value is string => Boolean(value))

    const input: Partial<Contact> = {
      ...contactState,
      addresses: preparedAddresses,
    }

    try {
      if (inEditMode || enableEdit) {
        if (onEditConfirm) {
          const status = await onEditConfirm(input)
          if (status === 'SUCCESS') {
            handleClose(true)
          }
          return
        }

        const idOrSelf = input.self ?? input.key
        if (!idOrSelf) {
          toast.error(
            t('common:feedback.error.editedContactError', {
              defaultValue: 'Could not update contact.',
            })
          )
          return
        }

        await updateContactMutation.mutateAsync({ idOrSelf, data: input })
        toast.success(
          t('contact:feedback.success.updateContact', {
            defaultValue: 'Contact updated successfully.',
          })
        )
        handleClose(true)
      } else {
        if (onConfirm) {
          const status = await onConfirm(input)
          if (status === 'SUCCESS') {
            handleClose(true)
          }
          return
        }
        await createContactMutation.mutateAsync(input)
        toast.success(
          t('contact:feedback.success.createContact', {
            defaultValue: 'Contact created successfully.',
          })
        )
        handleClose(true)
      }
    } catch (error) {
      log.error('Error saving contact', error)
      toast.error(
        t('contact:feedback.error.saveContact', {
          defaultValue: 'Could not save contact. Please try again.',
        })
      )
    }
  }

  /*const handleAddAddressOption = async (addressFormInput: any): Promise<'SUCCESS' | 'ERROR'> => {
    log.info('Begin creating address option')
    const json = formInputToAPIAddressJSON(addressFormInput)
    log.debug('Input ->', addressFormInput, ', JSON ->', json)

    // create a new address
    const [response, error] = await handleAPICallV1(
      HTTPMethod.POST,
      'v1/contacts/addresses/',
      undefined,
      json
    )

    if (!error && response) {
      log.info('Success creating address option')
      const newAddress = createAddress(response)

      // update addressStore
      addressStore.current = [...addressStore.current, newAddress]
      setAddressOptions(
        addressStore.current.map(addr => ({
          value: addr.self,
          label: getAddressRepresentation(addr, true, false),
        }))
      )

      setAddressListForm(prev => {
        const receiverFieldObj = prev.find(
          (fieldObj: AddressListFieldObject) => fieldObj.key === newAddressReceiver
        )
        receiverFieldObj!.value = {
          value: newAddress.self,
          label: getAddressRepresentation(newAddress, true, false),
        }
        return [...prev]
      })

      setNewAddressReceiver(undefined)
      return 'SUCCESS'
    } else {
      log.error('Error creating address option', error)
      return 'ERROR'
    }
  }*/

  const handleAddAddressToList = (): void => {
    setAddressListForm((prev: any) => {
      const fieldObjIndex: number = prev.length - 1
      prev[fieldObjIndex] = generateAddressFieldObject()
      return [...prev, generateAddressFieldObject('addAddress')]
    })
  }

  const handleRemoveAddressFromList = (addrFieldObj: any): void => {
    log.debug('handleRemoveAddressFromList', addrFieldObj)
    setAddressListForm((prev: any) => {
      const i = prev.indexOf(addrFieldObj)
      // remove addressFieldObject
      prev.splice(i, 1)

      return [...prev]
    })
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const formFieldsContact = GetConstant({ name: 'contactFormFields' }) as FormFieldType[]

  const renderAddressFieldObject = (addrFieldObj: AddressListFieldObject) => {
    if (addrFieldObj.type === 'addAddress') {
      return (
        <ListItem key={addrFieldObj.key}>
          <ListItemIcon>
            <Tooltip title={t('common:content.label.addAddress')} placement="right" arrow>
              <Button
                startIcon={<AddRounded style={{ fontSize: '25px', color: 'GrayText' }} />}
                onClick={handleAddAddressToList}
                style={{ fontSize: '16px', color: 'black' }}
              >
                {t('common:content.label.addAddress')}
              </Button>
            </Tooltip>
          </ListItemIcon>
        </ListItem>
      )
    } else if (addrFieldObj.type === 'savedAddress') {
      return (
        <ListItem key={addrFieldObj.key}>
          <ListItemIcon>
            <IconButton onClick={() => handleRemoveAddressFromList(addrFieldObj)}>
              <DeleteRounded />
            </IconButton>
          </ListItemIcon>
          <ListItemText primary={addrFieldObj.value?.label} />
        </ListItem>
      )
    } else if (addrFieldObj.type === 'selectAddress') {
      const otherSelectedSelfs = addressListForm
        .filter(f => f.key !== addrFieldObj.key && f.type !== 'addAddress' && Boolean(f.value))
        .map(f => f.value!.value)

      const currentFieldOptions = addressOptions.filter(
        opt => !otherSelectedSelfs.includes(opt.value)
      )

      return (
        <ListItem key={addrFieldObj.key}>
          <ListItemIcon>
            <IconButton onClick={() => handleRemoveAddressFromList(addrFieldObj)}>
              <DeleteRounded />
            </IconButton>
          </ListItemIcon>
          <ListItemText>
            <SearchSelectInput
              name={t('common:content.label.address')}
              fullWidth
              value={addrFieldObj.value ?? null}
              options={currentFieldOptions}
              onChange={(input: any) => {
                // update field object
                addrFieldObj.value = input
                // trigger rerender
                setAddressListForm(prev => [...prev])
              }}
              onAddOption={() => {
                setNewAddressReceiver(addrFieldObj.key)
                setOpenAddressDrawer(true)
              }}
            />
          </ListItemText>
        </ListItem>
      )
    }
  }
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  useEffect(() => {
    if (!open) {
      if (dataLoaded) {
        setDataLoaded(false)
      }
      return
    }

    if (!dataLoaded && formState.state) {
      // if the formState has a self or key property, it's an existing contact that should be edited
      // Empty objects with only empty string values (like emptyContact) should not trigger edit mode
      const isExistingContact = Boolean(formState.state.self || formState.state.key)
      setInEditMode(enableEdit || isExistingContact)

      if (isExistingContact) {
        if (Array.isArray(formState.state.addresses) && formState.state.addresses.length > 0) {
          const savedAddresses = (formState.state.addresses as Array<Address | string>).filter(
            (addr): addr is Address => typeof addr === 'object' && addr !== null && 'self' in addr
          )

          if (savedAddresses.length > 0) {
            setAddressListForm([
              ...savedAddresses.map((addr: Address) =>
                generateAddressFieldObject('savedAddress', {
                  value: addr.self,
                  label: getAddressRepresentation(addr, true, false),
                })
              ),
              generateAddressFieldObject('addAddress'),
            ])
          } else {
            setAddressListForm([generateAddressFieldObject('addAddress')])
          }
        } else {
          setAddressListForm([generateAddressFieldObject('addAddress')])
        }
      }

      setDataLoaded(true)
    }
  }, [open, dataLoaded, formState.state, enableEdit])
  /* ------------------------- Pre render actions end ------------------------- */

  log.debug('formState ->', formState.state, ', addressListForm ->', addressListForm)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        size="big"
        title={
          inEditMode ? t('common:content.label.editContact') : t('common:content.label.addContact')
        }
        onClose={handleClose}
        confirmButtonProps={{
          text: inEditMode
            ? t('common:interaction.button.save')
            : t('common:interaction.button.add'),
          onConfirm: () => {
            void handleConfirm()
          },
          disabled:
            missingRequiredFormFields([...formFieldsContact], formState.state) ||
            missingAddressSelectInput() ||
            isSaving,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('customer:content.label.contactData')}
        </Typography>
        <Grid spacing={2} container>
          <Form
            editing={true}
            formFields={formFieldsContact}
            formObject={formState.state ?? {}}
            onChange={(value: any) => formState.setState(value)}
          />
        </Grid>
        <Typography sx={{ mt: 4 }} variant="h6">
          {t('common:content.label.address')}
        </Typography>
        <List>
          {addressListForm.map((addrFieldObj: AddressListFieldObject) =>
            renderAddressFieldObject(addrFieldObj)
          )}
        </List>
      </MultilevelDrawer>
      {/* TODO: Fix address drawer
      <AddressDrawer
        open={openAddressDrawer}
        setOpen={setOpenAddressDrawer}
        formState={{
          state: formState.state,
          setState: formState.setState,
        }}
        onConfirm={(input: any) => handleAddAddressOption(input)}
      />*/}
    </>
  )
}
